import type {
    IProcess,
    IProcessesApp,
    IQueue,
    ODataQueryOptions,
    ODataResponse,
} from '@experiences/interfaces';
import { ProcessType } from '@experiences/interfaces';
import {
    get,
    getOrchestratorODataRoute,
} from '@experiences/util';
import buildQuery from 'odata-query';

const MAX_DATA_COUNT = 5;
const PROCESS_MANDATORY_PERMISSIONS = [ 'Processes.View', 'Jobs.View', 'Jobs.Create' ];
const QUEUE_MANDATORY_PERMISSIONS = [ 'Queues.View', 'Transactions.View', 'Transactions.Create' ];

interface IProcessVersionOData {
    VersionNumber: string;
    CreationTime: Date;
}

interface IProcessOData {
    Id: number;
    Name: string;
    Status?: string;
    Description?: string;
    CreationTime: Date;
    OrganizationUnitFullyQualifiedName: string;
    CurrentVersion: IProcessVersionOData;
}

function validateTenant(tenantName: string) {
    if (!tenantName || tenantName === 'null') {
        throw new Error('Invalid tenant');
    }
}

function validateData(data: any) {
    if (!data?.value) {
        throw new Error('Invalid data response');
    }
}

function getPermissionQuery(entityType: ProcessType): string {
    let query = '';
    const permissions = entityType === ProcessType.Process
        ? PROCESS_MANDATORY_PERMISSIONS
        : QUEUE_MANDATORY_PERMISSIONS;
    permissions.forEach(permission => {
        query += `&mandatoryPermissions=${permission}`;
    });
    return query;
}

function getSortedProcesses(data: IProcessesApp[]): IProcessesApp[] {
    return data
        .sort((a, b) => new Date(b.Entity.CreationTime).getTime() - new Date(a.Entity.CreationTime).getTime())
        .splice(0, MAX_DATA_COUNT);
}

export async function getProcesses(accessToken: string, tenantName: string, accountLogicalName: string): Promise<IProcessesApp[]> {
    validateTenant(tenantName);

    const baseUrl = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/`;

    const processQueryParam: ODataQueryOptions = {
        select: [ 'Id', 'Name', 'Description', 'OrganizationUnitFullyQualifiedName' ],
        count: true,
        top: MAX_DATA_COUNT,
        expand: { CurrentVersion: { select: [ 'VersionNumber', 'CreationTime' ] } },
        orderBy: [ 'CreationTime desc' ],
        filter: { and: [ { ProcessType: { eq: 'Process' } } ] },
    };
    const processPermissionQuery = getPermissionQuery(ProcessType.Process);
    const processUrl = `${baseUrl}Releases${buildQuery(processQueryParam)}${processPermissionQuery}`;

    const queueQueryParam: ODataQueryOptions = {
        select: [ 'Id', 'Name', 'Description', 'CreationTime', 'OrganizationUnitFullyQualifiedName' ],
        top: MAX_DATA_COUNT,
        orderBy: [ 'CreationTime desc' ],
    };
    const queuePermissionQuery = getPermissionQuery(ProcessType.Queue);
    const queueUrl = `${baseUrl}QueueDefinitions${buildQuery(queueQueryParam)}${queuePermissionQuery}`;

    const [ processResponse, queueResponse ] = await Promise.all([
        get<ODataResponse<IProcessOData>>(processUrl, {
            ignoreBasePath: true,
            accessToken,
        }),
        get<ODataResponse<IQueue>>(queueUrl, {
            ignoreBasePath: true,
            accessToken,
        }),
    ]);

    validateData(processResponse);
    validateData(queueResponse);

    let processes: IProcessesApp[] = [];

    queueResponse.value.forEach((queue: IQueue) => {
        processes.push({
            Entity: queue,
            Type: ProcessType.Queue,
        });
    });

    processResponse.value.forEach((processOData: IProcessOData) => {
        const process: IProcess = {
            Id: processOData.Id,
            Name: processOData.Name,
            Status: processOData.Status ?? '',
            Description: processOData.Description,
            CreationTime: processOData.CurrentVersion.CreationTime,
            OrganizationUnitFullyQualifiedName: processOData.OrganizationUnitFullyQualifiedName,
            CurrentVersion: {
                Id: 0,
                VersionNumber: processOData.CurrentVersion.VersionNumber,
                creationTime: processOData.CurrentVersion.CreationTime,
            },
        };
        processes.push({
            Entity: process,
            Type: ProcessType.Process,
        });
    });

    processes = getSortedProcesses(processes);

    return processes;
}
